import type { Event } from '@tool-factory/registry';
import { useEffect, useState } from 'react';
import { Pill, SectionHead, StatusBar } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import { supabase } from '../lib/supabase';

export function EventsFeed() {
  const [events, setEvents] = useState<Event[]>([]);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('tf_events')
      .select('*')
      .order('occurred_at', { ascending: false })
      .limit(100)
      .then(({ data }) => {
        if (!cancelled && data) setEvents(data);
      });
    const ch = supabase
      .channel('events-feed')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'tf_events' },
        (payload) => {
          setEvents((prev) => [payload.new as Event, ...prev].slice(0, 200));
        },
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') setConnected(true);
      });
    return () => {
      cancelled = true;
      supabase.removeChannel(ch);
    };
  }, []);

  return (
    <div>
      <PageHeader
        breadcrumb="Tool Factory · Events"
        title="Events"
        subtitle="Live event stream from every tool. Updates in real time as users interact."
        actions={
          <div className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-[12px] font-medium">
            <span
              className={`inline-block h-[6px] w-[6px] rounded-full ${
                connected ? 'bg-[#2f7d4f]' : 'bg-foreground/35'
              }`}
            />
            <span className="text-foreground/70">{connected ? 'Live' : 'Connecting…'}</span>
          </div>
        }
      />

      <SectionHead
        title="Live stream"
        meta={`${events.length} event${events.length === 1 ? '' : 's'}`}
      />

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {events.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-foreground/50">
            No events yet. Activity from your tools will appear here in real time.
          </div>
        ) : (
          <ul className="divide-y divide-border/50">
            {events.map((e) => (
              <li
                key={e.id}
                className="flex items-center gap-4 px-5 py-3 hover:bg-secondary/35 transition-colors duration-150"
              >
                <span className="font-mono text-[11px] text-foreground/55 w-28 flex-shrink-0">
                  {new Date(e.occurred_at).toLocaleTimeString()}
                </span>
                <Pill>{e.event_type}</Pill>
                <span className="flex-1 font-mono text-[11px] text-foreground/55">
                  tool={e.tool_id.slice(0, 8)} · session={e.session_id.slice(0, 8)}
                </span>
                {e.user_agent_bot ? (
                  <span className="text-[11px] uppercase tracking-[0.12em] font-semibold text-gold">
                    bot
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <StatusBar
        left={`${events.length} events shown`}
        right={connected ? 'live · realtime' : 'offline'}
      />
    </div>
  );
}
